import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Bot,
  CalendarDays,
  Check,
  Database,
  Headphones,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  UserRound,
  UserRoundCheck,
  Workflow,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { COMPANY_BRAND_NAME } from '../../support/brand';
import {
  CLOSING,
  CONTACT_URL,
  CONTROL_POINTS,
  CONTROL_SECTION,
  FAQS,
  FAQ_SECTION,
  HERO,
  HOW_SECTION,
  LAST_MODIFIED_LABEL,
  OUTCOMES,
  OUTCOMES_SECTION,
  RELATED_LINKS,
  REGISTER_URL,
  SEO,
  STEPS,
  TRUST,
  USE_CASES,
  USE_CASES_SECTION,
} from './content';
import { META_TAGS, STRUCTURED_DATA } from './seo';
import '../../styles/whatsapp-landing.css';

const OUTCOME_ICONS = {
  answer: MessageCircle,
  qualify: UserRoundCheck,
  act: Zap,
  handoff: Headphones,
};

const USE_CASE_ICONS = {
  sales: ShoppingBag,
  support: Headphones,
  scheduling: CalendarDays,
  education: BookOpen,
};

function metadataSelector(tagName, attributes) {
  if (tagName === 'meta') {
    return attributes.name
      ? `meta[name="${attributes.name}"]`
      : `meta[property="${attributes.property}"]`;
  }
  return `link[rel="${attributes.rel}"]${attributes.type ? `[type="${attributes.type}"]` : ''}`;
}

function useLandingMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;

    const managedElements = META_TAGS.map(([tagName, attributes]) => {
      const existing = document.head.querySelector(metadataSelector(tagName, attributes));
      const element = existing || document.createElement(tagName);
      const previousAttributes = existing
        ? Array.from(existing.attributes).map(({ name, value }) => [name, value])
        : [];
      const removeOnCleanup = !existing || existing.dataset.whatsappStatic === 'true';

      element.dataset.whatsappSeo = 'true';
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      if (!existing) document.head.appendChild(element);

      return { element, previousAttributes, removeOnCleanup };
    });

    document.head.querySelectorAll('script[data-whatsapp-static="true"]').forEach((item) => {
      item.remove();
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.whatsappSeo = 'true';
    script.text = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);

    return () => {
      document.title = previousTitle === SEO.title ? COMPANY_BRAND_NAME : previousTitle;
      managedElements.forEach(({ element, previousAttributes, removeOnCleanup }) => {
        if (removeOnCleanup) {
          element.remove();
          return;
        }
        Array.from(element.attributes).forEach(({ name }) => element.removeAttribute(name));
        previousAttributes.forEach(([name, value]) => element.setAttribute(name, value));
      });
      script.remove();
    };
  }, []);
}

function PrimaryCta({ isAuthenticated, className = '' }) {
  if (isAuthenticated) {
    return (
      <Link to="/onboarding" className={`wa-btn wa-btn-primary ${className}`}>
        Criar agente
        <ArrowRight aria-hidden="true" />
      </Link>
    );
  }

  return (
    <a href={REGISTER_URL} className={`wa-btn wa-btn-primary ${className}`}>
      Criar meu agente
      <ArrowRight aria-hidden="true" />
    </a>
  );
}

function MetaTrustBadge() {
  return (
    <div className="wa-meta-badge" aria-label={`${TRUST.label}. ${TRUST.detail}`}>
      <span className="wa-meta-mark" aria-hidden="true">
        ∞
      </span>
      <span>
        <strong>{TRUST.label}</strong>
        <small>{TRUST.detail}</small>
      </span>
    </div>
  );
}

function HeroIllustration() {
  return (
    <div className="wa-hero-visual" data-whatsapp-hero-illustration="true" aria-hidden="true">
      <div className="wa-orbit wa-orbit-one" />
      <div className="wa-orbit wa-orbit-two" />
      <div className="wa-floating-chip wa-chip-knowledge">
        <Database />
        Base conectada
      </div>
      <div className="wa-floating-chip wa-chip-action">
        <Zap />
        Tarefa executada
      </div>

      <div className="wa-phone">
        <div className="wa-phone-top">
          <span className="wa-avatar">
            <Bot />
          </span>
          <span>
            <strong>Agente MAIA</strong>
            <small>online agora</small>
          </span>
          <span className="wa-online-dot" />
        </div>

        <div className="wa-phone-chat">
          <div className="wa-message wa-message-user">
            Olá! Preciso de uma solução para minha equipe comercial.
            <small>14:28</small>
          </div>
          <div className="wa-message wa-message-agent wa-message-delay-one">
            Posso te ajudar. Quantas pessoas atendem pelo WhatsApp hoje?
            <small>14:28</small>
          </div>
          <div className="wa-message wa-message-user wa-message-delay-two">
            Somos 12 e queremos responder mais rápido.
            <small>14:29</small>
          </div>
          <div className="wa-message wa-message-agent wa-message-delay-three">
            Entendi. Vou registrar seu cenário e indicar o melhor próximo passo.
            <small>14:29</small>
          </div>
        </div>

        <div className="wa-phone-status">
          <span>
            <Sparkles />
            Lead qualificado
          </span>
          <strong>Pronto para vendas</strong>
        </div>
      </div>
    </div>
  );
}

function ConversationPreview({ item }) {
  return (
    <div className="wa-case-preview" aria-hidden="true">
      <div className="wa-case-preview-top">
        <span className="wa-case-avatar">
          <UserRound />
        </span>
        <span>Nova conversa</span>
        <span className="wa-case-live">agente ativo</span>
      </div>
      <div className="wa-case-message wa-case-user">{item.example}</div>
      <div className="wa-case-message wa-case-agent">{item.response}</div>
    </div>
  );
}

function WorkflowIllustration() {
  return (
    <div className="wa-workflow" aria-label="Fluxo do contato até a equipe">
      <div className="wa-workflow-node">
        <span>
          <UserRound />
        </span>
        <strong>Contato</strong>
        <small>manda uma mensagem</small>
      </div>
      <span className="wa-workflow-line" aria-hidden="true">
        <i />
      </span>
      <div className="wa-workflow-node wa-workflow-main">
        <span>
          <Bot />
        </span>
        <strong>Agente MAIA</strong>
        <small>entende e resolve</small>
      </div>
      <span className="wa-workflow-line" aria-hidden="true">
        <i />
      </span>
      <div className="wa-workflow-node">
        <span>
          <Workflow />
        </span>
        <strong>Próximo passo</strong>
        <small>ação ou equipe</small>
      </div>
    </div>
  );
}

function WhatsAppLanding() {
  const { isAuthenticated } = useAuth();
  useLandingMetadata();

  return (
    <div className="wa-landing">
      <section className="wa-hero">
        <div className="wa-hero-glow wa-hero-glow-left" aria-hidden="true" />
        <div className="wa-hero-glow wa-hero-glow-right" aria-hidden="true" />
        <div className="wa-container wa-hero-grid">
          <div className="wa-hero-copy">
            <MetaTrustBadge />
            <p className="wa-eyebrow">{HERO.eyebrow}</p>
            <h1>{HERO.headline}</h1>
            <p className="wa-hero-body">{HERO.body}</p>
            <div className="wa-hero-actions">
              <PrimaryCta isAuthenticated={isAuthenticated} />
              <a href={CONTACT_URL} className="wa-btn wa-btn-secondary">
                Falar com a MAIA
              </a>
            </div>
            <ul className="wa-hero-proof" aria-label="Recursos principais">
              <li>
                <Check aria-hidden="true" /> Conhecimento próprio
              </li>
              <li>
                <Check aria-hidden="true" /> Transferência para pessoas
              </li>
              <li>
                <Check aria-hidden="true" /> Integrações e tarefas
              </li>
            </ul>
          </div>
          <HeroIllustration />
        </div>
      </section>

      <section className="wa-section wa-outcomes" id="features">
        <div className="wa-container">
          <div className="wa-section-heading">
            <p className="wa-eyebrow">{OUTCOMES_SECTION.eyebrow}</p>
            <h2>{OUTCOMES_SECTION.headline}</h2>
            <p>{OUTCOMES_SECTION.body}</p>
          </div>
          <div className="wa-outcome-grid">
            {OUTCOMES.map((item) => {
              const Icon = OUTCOME_ICONS[item.id];
              return (
                <article className="wa-outcome-card" key={item.id}>
                  <span className="wa-card-number">{item.number}</span>
                  <span className="wa-card-icon">
                    <Icon aria-hidden="true" />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="wa-section wa-use-cases" id="casos-de-uso">
        <div className="wa-container">
          <div className="wa-section-heading wa-section-heading-light">
            <p className="wa-eyebrow">{USE_CASES_SECTION.eyebrow}</p>
            <h2>{USE_CASES_SECTION.headline}</h2>
          </div>
          <div className="wa-case-grid">
            {USE_CASES.map((item) => {
              const Icon = USE_CASE_ICONS[item.id];
              return (
                <article className="wa-case-card" key={item.id}>
                  <div className="wa-case-label">
                    <Icon aria-hidden="true" />
                    {item.label}
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <ConversationPreview item={item} />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="wa-section wa-how" id="como-funciona">
        <div className="wa-container">
          <div className="wa-section-heading">
            <p className="wa-eyebrow">{HOW_SECTION.eyebrow}</p>
            <h2>{HOW_SECTION.headline}</h2>
          </div>
          <ol className="wa-step-grid">
            {STEPS.map((step) => (
              <li key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
          <div className="wa-api-strip">
            <MetaTrustBadge />
            <p>
              A conexão usa a infraestrutura oficial do WhatsApp para enviar e receber
              mensagens com segurança e continuidade operacional.
            </p>
          </div>
        </div>
      </section>

      <section className="wa-section wa-control">
        <div className="wa-container wa-control-grid">
          <div>
            <p className="wa-eyebrow">{CONTROL_SECTION.eyebrow}</p>
            <h2>{CONTROL_SECTION.headline}</h2>
            <p className="wa-control-body">{CONTROL_SECTION.body}</p>
            <ul className="wa-control-list">
              {CONTROL_POINTS.map((point) => (
                <li key={point}>
                  <span>
                    <Check aria-hidden="true" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <WorkflowIllustration />
        </div>
      </section>

      <section className="wa-section wa-faq" id="duvidas">
        <div className="wa-container wa-faq-grid">
          <div className="wa-faq-heading">
            <p className="wa-eyebrow">{FAQ_SECTION.eyebrow}</p>
            <h2>{FAQ_SECTION.headline}</h2>
            <p>{`Conteúdo atualizado em ${LAST_MODIFIED_LABEL}.`}</p>
          </div>
          <div className="wa-faq-list">
            {FAQS.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wa-closing">
        <div className="wa-closing-orb" aria-hidden="true" />
        <div className="wa-container">
          <Sparkles className="wa-closing-icon" aria-hidden="true" />
          <h2>{CLOSING.headline}</h2>
          <p>{CLOSING.body}</p>
          <div className="wa-closing-actions">
            <PrimaryCta isAuthenticated={isAuthenticated} />
            <a href={CONTACT_URL} className="wa-btn wa-btn-light">
              Conversar no WhatsApp
            </a>
          </div>
          <nav className="wa-closing-actions" aria-label="Conheça outras soluções da MAIA">
            {RELATED_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="wa-btn wa-btn-secondary">
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      </section>
    </div>
  );
}

export default WhatsAppLanding;
